import { useEffect, useMemo, useRef, useState } from "react";
import Spinner from "./../ui/Spinner";
import WelcomePopupPreview from "./WelcomePopupPreview";
import { Help, HowItWorks, btnGhost, errMsg, inputCls, labelCls } from "./promotionsUi";
import type { Toast } from "./campaignUi";
import { adminService } from "../../features/admin/service";
import { promotionsService } from "../../features/promotions/service";
import type { AudienceUser, Campaign, GrantMode, PromoRole } from "../../features/promotions/types";

// The audience modal and the recipients drawer live in CampaignAudienceModal.tsx
// and CampaignRecipientsDrawer.tsx.

export const DEFAULT_NOTIFY_TITLE = "Festive season bonus!";
export const DEFAULT_NOTIFY_MESSAGE =
  "{credits} extra credits are waiting for you this festive season. Enjoy, create something amazing, and share your feedback with us.";

const MAX_PICKED_SHOWN = 10;

// ── Campaign modal section ───────────────────────────────────────────────────
export interface PickedUser {
  id: string;
  label: string;
}

export interface ExistingDraft {
  include_existing: boolean;
  grant_mode: GrantMode;
  audience_roles: PromoRole[];
  audience_companies: string[];
  extra_users: PickedUser[];
  notify: boolean;
  notify_title: string;
  notify_message: string;
}

export function emptyExistingDraft(): ExistingDraft {
  return {
    include_existing: false,
    grant_mode: "admin_apply",
    audience_roles: [],
    audience_companies: [],
    extra_users: [],
    notify: false,
    notify_title: "",
    notify_message: "",
  };
}

export function existingDraftFrom(c: Campaign): ExistingDraft {
  return {
    include_existing: !!c.include_existing,
    grant_mode: c.grant_mode ?? "admin_apply",
    audience_roles: c.audience_roles ?? [],
    audience_companies: c.audience_companies ?? [],
    extra_users: (c.extra_user_ids ?? []).map((id) => {
      const u = (c.extra_users ?? []).find((x) => x.id === id);
      const label = u ? [u.name, u.email].filter(Boolean).join(" · ") : "";
      return { id, label: label || "Unknown user (deleted?)" };
    }),
    notify: !!c.notify,
    notify_title: c.notify_title ?? "",
    notify_message: c.notify_message ?? "",
  };
}

const userLabel = (u: Pick<AudienceUser, "id" | "name" | "email">) => [u.name, u.email].filter(Boolean).join(" · ") || "Unknown user";

function Chip({ children, onRemove }: { children: React.ReactNode; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 bg-indigo-500/15 text-indigo-200 border border-indigo-500/30 rounded-full pl-2.5 pr-1 py-0.5 text-xs max-w-full">
      <span className="truncate">{children}</span>
      <button type="button" onClick={onRemove} className="px-1 text-indigo-300 hover:text-white" aria-label="Remove">×</button>
    </span>
  );
}

export function ExistingUsersSection({
  value,
  onChange,
  enabledRoles,
  sellerCredits,
  buyerCredits,
  campaignName,
  toast,
  locked = false,
}: {
  value: ExistingDraft;
  onChange: (p: Partial<ExistingDraft>) => void;
  enabledRoles: PromoRole[];
  sellerCredits: string;
  buyerCredits: string;
  campaignName: string;
  toast: Toast;
  locked?: boolean;
}) {
  const [companies, setCompanies] = useState<string[]>([]);
  const [companyQ, setCompanyQ] = useState("");
  const [userQ, setUserQ] = useState("");
  const [userHits, setUserHits] = useState<AudienceUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [paste, setPaste] = useState("");
  const [resolving, setResolving] = useState(false);
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const [matchedCount, setMatchedCount] = useState<number | null>(null);

  useEffect(() => {
    if (!value.include_existing) return;
    adminService.getCompanyNames().then(setCompanies).catch(() => setCompanies([]));
  }, [value.include_existing]);

  // Debounced user search (existing admin users endpoint).
  const reqId = useRef(0);
  useEffect(() => {
    const q = userQ.trim();
    if (!value.include_existing || q.length < 2) {
      setUserHits([]);
      return;
    }
    const id = ++reqId.current;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await adminService.getUsers({ search: q, page: 1, page_size: MAX_PICKED_SHOWN });
        if (id !== reqId.current) return;
        setUserHits(
          (res.items ?? []).map((u: any) => ({
            id: String(u.id ?? u._id),
            name: u.name ?? null,
            email: u.email ?? null,
            role: u.role ?? null,
            company: [u.company_name, u.business_name, u.agency_name].find((v) => typeof v === "string" && v.trim()) ?? null,
          })),
        );
      } catch {
        if (id === reqId.current) setUserHits([]);
      } finally {
        if (id === reqId.current) setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [userQ, value.include_existing]);

  const pickedIds = useMemo(() => new Set(value.extra_users.map((u) => u.id)), [value.extra_users]);
  const toggleUser = (u: AudienceUser) => {
    if (pickedIds.has(u.id)) onChange({ extra_users: value.extra_users.filter((x) => x.id !== u.id) });
    else onChange({ extra_users: [...value.extra_users, { id: u.id, label: userLabel(u) }] });
  };

  const resolvePaste = async () => {
    const identifiers = Array.from(new Set(paste.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean)));
    if (!identifiers.length) return toast.showError("Paste at least one email or user id");
    setResolving(true);
    try {
      const res = await promotionsService.resolveUsers(identifiers);
      const merged = [...value.extra_users];
      res.matched.forEach((u) => {
        if (!merged.some((x) => x.id === u.id)) merged.push({ id: u.id, label: userLabel(u) });
      });
      onChange({ extra_users: merged });
      setUnmatched(res.unmatched);
      setMatchedCount(res.matched.length);
      if (!res.unmatched.length) setPaste("");
    } catch (e) {
      toast.showError(errMsg(e, "Failed to look up users"));
    } finally {
      setResolving(false);
    }
  };

  const toggleRole = (r: PromoRole) =>
    onChange({
      audience_roles: value.audience_roles.includes(r) ? value.audience_roles.filter((x) => x !== r) : [...value.audience_roles, r],
    });
  const toggleCompany = (c: string) =>
    onChange({
      audience_companies: value.audience_companies.includes(c) ? value.audience_companies.filter((x) => x !== c) : [...value.audience_companies, c],
    });

  const visibleCompanies = companies.filter((c) => c.toLowerCase().includes(companyQ.trim().toLowerCase())).slice(0, 100);
  const claim = value.grant_mode === "claim";

  return (
    <div className="border border-gray-800 rounded-xl p-4 space-y-4">
      {locked && <p className="text-xs text-amber-300">Locked while an Apply is running</p>}
      <div>
        <label className="flex items-center gap-2 text-sm text-white font-medium">
          <input type="checkbox" checked={value.include_existing} disabled={locked} onChange={(e) => onChange({ include_existing: e.target.checked })} />
          Include existing users
        </label>
        <Help>Also give this campaign's credits to people who already finished onboarding. Off = only new users finishing onboarding get it.</Help>
      </div>

      {value.include_existing && (
        <>
          <HowItWorks>
            <p>Save the campaign first, then use "Preview audience" on its row to see exactly who qualifies and the total credits it would cost. Nothing is granted until you confirm.</p>
            <p>The audience is everyone in ANY of the three groups below: all users of the roles you tick, all users of the companies you pick, and the individual users you add. Tick nothing and nobody is targeted. Only onboarded, verified, not disabled, not restricted users qualify; others are skipped and counted by reason.</p>
            <p>Each user gets the campaign amount at most once, even if you apply again. Users who already got it through onboarding are skipped.</p>
            <p>In the notice copy, {"{credits}"} is the amount and {"{campaign}"} is this campaign's name ({"{name}"} also works, as an old alias for the campaign name; it is never the user's name).</p>
          </HowItWorks>

          <div>
            <span className={labelCls}>How are credits given?</span>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-300">
                <input type="radio" name="grant_mode" className="mt-1" checked={!claim} disabled={locked} onChange={() => onChange({ grant_mode: "admin_apply" })} />
                <span>
                  <span className="text-white">Admin applies</span>
                  <span className="block text-xs text-gray-500">You review the audience, confirm the cost, and credits are added to wallets right away.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-300">
                <input type="radio" name="grant_mode" className="mt-1" checked={claim} disabled={locked} onChange={() => onChange({ grant_mode: "claim" })} />
                <span>
                  <span className="text-white">User claims</span>
                  <span className="block text-xs text-gray-500">Users get a notice and the credits are added only when they tap Claim, while the campaign is live.</span>
                </span>
              </label>
            </div>
            <Help>Newly onboarded users always get the amount automatically; Claim applies to users who already onboarded.</Help>
          </div>

          <div>
            <span className={labelCls}>Everyone of a role</span>
            <div className="flex gap-4">
              {(["seller", "buyer"] as PromoRole[]).map((r) => (
                <label key={r} className={`flex items-center gap-2 text-sm capitalize ${enabledRoles.includes(r) ? "text-gray-300" : "text-gray-600"}`}>
                  <input type="checkbox" disabled={locked || !enabledRoles.includes(r)} checked={value.audience_roles.includes(r)} onChange={() => toggleRole(r)} />
                  {r}s
                </label>
              ))}
            </div>
            <Help>Tick Sellers for all eligible sellers, Buyers for all eligible buyers, or both for all eligible users. Untick both to pick by company or by name below. Must be roles this campaign covers.</Help>
          </div>

          <div>
            <span className={labelCls}>Companies (optional)</span>
            {value.audience_companies.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {value.audience_companies.map((c) => (
                  <Chip key={c} onRemove={() => toggleCompany(c)}>{c}</Chip>
                ))}
              </div>
            )}
            <input className={inputCls} value={companyQ} onChange={(e) => setCompanyQ(e.target.value)} placeholder="Search company names, e.g. Acme Travels" />
            <div className="mt-1 max-h-36 overflow-y-auto border border-gray-800 rounded-lg divide-y divide-gray-800/60">
              {visibleCompanies.length === 0 ? (
                <p className="text-xs text-gray-500 px-3 py-2">{companies.length ? "No matching company." : "No company names found."}</p>
              ) : (
                visibleCompanies.map((c) => (
                  <label key={c} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800/60 cursor-pointer">
                    <input type="checkbox" disabled={locked} checked={value.audience_companies.includes(c)} onChange={() => toggleCompany(c)} />
                    <span className="truncate">{c}</span>
                  </label>
                ))
              )}
            </div>
            <Help>Also include everyone from these companies, whatever their role (matched ignoring capitals). Leave empty to skip this group.</Help>
          </div>

          <div>
            <span className={labelCls}>Pick specific users (optional)</span>
            <input className={inputCls} value={userQ} onChange={(e) => setUserQ(e.target.value)} placeholder="Search by name, email or phone (2+ letters)" />
            {(searching || userHits.length > 0) && (
              <div className="mt-1 max-h-44 overflow-y-auto border border-gray-800 rounded-lg divide-y divide-gray-800/60">
                {searching && <div className="flex justify-center py-2"><Spinner /></div>}
                {userHits.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800/60 cursor-pointer">
                    <input type="checkbox" disabled={locked} checked={pickedIds.has(u.id)} onChange={() => toggleUser(u)} />
                    <span className="min-w-0">
                      <span className="block truncate text-white">{u.name || "(no name)"}</span>
                      <span className="block truncate text-xs text-gray-500">{u.email} · {u.role}{u.company ? ` · ${u.company}` : ""}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
            <Help>Also include these individual users, even if they are not in a role or company above. They still must be eligible (onboarded, verified).</Help>
          </div>

          <div>
            <span className={labelCls}>Paste emails or user ids (optional)</span>
            <textarea
              className={inputCls}
              rows={3}
              disabled={locked}
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              placeholder={"priya@example.com\nrahul@example.com, 665f1c2e9a1b2c3d4e5f6a7b"}
            />
            <div className="flex items-center gap-2 mt-1">
              <button type="button" onClick={resolvePaste} disabled={locked || resolving || !paste.trim()} className={`${btnGhost} border border-gray-700 disabled:opacity-50`}>
                {resolving ? "Checking…" : "Find these users"}
              </button>
              {matchedCount !== null && <span className="text-xs text-emerald-400">{matchedCount} matched and added</span>}
            </div>
            <Help>One per line or comma separated. Matched users are added below; unmatched entries are listed so you can fix them.</Help>
            {unmatched.length > 0 && (
              <div className="mt-2 text-xs bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-red-300">
                <p className="font-medium mb-1">{unmatched.length} not found:</p>
                <p className="break-all">{unmatched.join(", ")}</p>
              </div>
            )}
          </div>

          {value.extra_users.length > 0 && (
            <div>
              <span className={labelCls}>Extra users added ({value.extra_users.length})</span>
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                {value.extra_users.map((u) => (
                  <Chip key={u.id} onRemove={() => onChange({ extra_users: value.extra_users.filter((x) => x.id !== u.id) })}>{u.label}</Chip>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-gray-800 pt-4 space-y-3">
            <div>
              <label className="flex items-center gap-2 text-sm text-white font-medium">
                <input type="checkbox" checked={value.notify} onChange={(e) => onChange({ notify: e.target.checked })} />
                Notify users
              </label>
              <Help>Sends an in-app, push and live notification with an {claim ? "Claim" : "Acknowledge"} button. Also covers users who got this amount at onboarding.</Help>
            </div>
            {value.notify && (
              <>
                <div>
                  <label className={labelCls}>Notice title (optional)</label>
                  <input className={inputCls} value={value.notify_title} onChange={(e) => onChange({ notify_title: e.target.value })} maxLength={120} placeholder={DEFAULT_NOTIFY_TITLE} />
                  <Help>Use {"{credits}"} for the amount and {"{campaign}"} for this campaign's name. Blank = the default shown.</Help>
                </div>
                <div>
                  <label className={labelCls}>Notice message (optional)</label>
                  <textarea className={inputCls} rows={3} value={value.notify_message} onChange={(e) => onChange({ notify_message: e.target.value })} maxLength={500} placeholder={DEFAULT_NOTIFY_MESSAGE} />
                  <Help>Use {"{credits}"} and {"{campaign}"} (the campaign name, not the user's name). Blank = the default message shown.</Help>
                </div>
                <WelcomePopupPreview
                  sellerCredits={sellerCredits}
                  buyerCredits={buyerCredits}
                  title={value.notify_title}
                  message={value.notify_message}
                  campaignName={campaignName.trim() || null}
                  defaultTitle={DEFAULT_NOTIFY_TITLE}
                  defaultMessage={DEFAULT_NOTIFY_MESSAGE}
                  notice={{ primaryLabel: claim ? "Claim credits" : "Acknowledge" }}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
